import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './ArticleDetail.css';
import { API } from '../services/api';
import { getUserId } from '../utils/auth';
import HeartIcon from './HeartIcon';
import BookmarkIcon from './BookmarkIcon';
import ImageCarousel from './ImageCarousel';
import { useAuth } from '../context/AuthContext';

const ArticleDetail = ({ article, onClose, onFavoriteChange, onReadLaterChange, onRead }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [isReadLater, setIsReadLater] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { currentUser } = useAuth();
  const plan = currentUser?.plan || 'none';
  const isLocked = !currentUser || plan === 'none';

  // Restrictions removed - all users can see everything
  const isPatent = article?.Type === 'Patent';
  const shouldBlur = false;
  const shouldHideButtons = false;

  // Initialiser les états au chargement
  useEffect(() => {
    if (!article) return;

    setIsFavorite(article.isFavorite || false);
    setIsReadLater(article.isReadLater || false);
  }, [article]);

  // Marquer comme lu à l'ouverture de la carte (si onRead fourni)
  useEffect(() => {
    if (article && typeof onRead === 'function') {
      onRead(article._id || article.ID || article.id, article.ID ?? article.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.ID, article?._id, article?.id]);

  // Gérer le clic sur le bouton de favori
  const handleToggleFavorite = async () => {
    if (!article || isLoading) return;
    const userId = getUserId();
    setIsLoading(true);
    const newFavoriteState = !isFavorite;
    setIsFavorite(newFavoriteState);
    try {
      if (isFavorite) {
        // Suppression du favori (API RESTful typique)
        await API.delete(`/favorites/${article.ID}`, {
          params: { userId }
        });
      } else {
        // Ajout favori
        await API.post('/favorites', {
          userId,
          articleId: article.ID
        });
      }
      if (onClose) onClose();
      if (onFavoriteChange) onFavoriteChange(article.ID, newFavoriteState);
      // Toast cohérent
      const message = newFavoriteState
        ? `"${article.Title}" has been added to your favorites`
        : `"${article.Title}" has been removed from your favorites`;
      if (window.showToast) window.showToast(message, 'success');
    } catch (error) {
      setIsFavorite(!newFavoriteState);
      setIsLoading(false);
      if (window.showToast) window.showToast("Error while modifying favorites", 'error');
    }
  };

  // Gérer le clic sur le bouton de lecture différée
  const handleToggleReadLater = async () => {
    if (!article || isLoading) return;
    const userId = getUserId();
    setIsLoading(true);
    const newReadLaterState = !isReadLater;
    setIsReadLater(newReadLaterState);
    try {
      if (isReadLater) {
        // Suppression lecture différée (API RESTful typique)
        await API.delete(`/readlater/${article.ID}`, {
          params: { userId }
        });
      } else {
        // Ajout lecture différée
        await API.post('/readlater', {
          userId,
          articleId: article.ID
        });
      }
      if (onClose) onClose();
      if (onReadLaterChange) onReadLaterChange(article.ID, newReadLaterState);
      // Toast cohérent
      const message = newReadLaterState
        ? `"${article.Title}" has been added to your reading list`
        : `"${article.Title}" has been removed from your reading list`;
      if (window.showToast) window.showToast(message, 'success');
    } catch (error) {
      setIsReadLater(!newReadLaterState);
      setIsLoading(false);
      if (window.showToast) window.showToast("Error while modifying reading list", 'error');
    }
  };

  // Copier le lien source de l'article
  const handleCopyLink = () => {
    // Utiliser le lien source de l'article
    const linkToCopy = article.Link;
    
    // Copier dans le presse-papier
    navigator.clipboard.writeText(linkToCopy)
      .then(() => {
        if (window.showToast) {
          window.showToast('Link copied to clipboard', 'success');
        }
      })
      .catch(err => {
        console.error('Error copying link:', err);
        if (window.showToast) {
          window.showToast('Error copying link', 'error');
        }
      });
  };

  // Partager sur LinkedIn
  const handleShareLinkedIn = () => {
    // Utiliser le lien source de l'article
    const articleUrl = encodeURIComponent(article.Link);
    const title = encodeURIComponent(article.Title);
    const summary = encodeURIComponent(article.short_resume || '');
    
    // Construire l'URL de partage LinkedIn
    const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${articleUrl}&title=${title}&summary=${summary}`;
    
    // Ouvrir dans une nouvelle fenêtre
    window.open(linkedInShareUrl, '_blank', 'width=600,height=600');
  };

  // Ouvrir le lien source
  const handleOpenSource = (e) => {
    e.preventDefault();
    window.open(article.Link, '_blank');
  };

  // Gérer le clic sur l'overlay (fermer le modal)
  const handleOverlayClick = (e) => {
    // Fermer seulement si on clique sur l'overlay, pas sur le contenu
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // Fonction pour obtenir la couleur du tag en fonction du type
  const getTagColor = (type) => {
    switch (type) {
      case 'OEM':
        return 'tag-oem';
      case 'brand':
        return 'tag-brand';
      case 'car_model':
        return 'tag-car-model';
      case 'System':
        return 'tag-system';
      case 'subsystem':
      case 'Subsystem':
        return 'tag-subsystem';
      case 'part':
        return 'tag-part';
      case 'Type':
        return 'tag-type';
      case 'Material':
        return 'tag-material';
      case 'technology':
        return 'tag-technology';
      default:
        return '';
    }
  };

  if (!article) return null;

  // Préparer les images pour le carrousel
  const images = [
    article.Picture,
    article.Picture2,
    article.Picture3
  ].filter(Boolean); // Filtrer les valeurs null ou undefined

  const modalContent = (
    <div className="article-detail-overlay" onClick={handleOverlayClick}>
      <div className="article-detail-container">
        <div className="article-detail-header">
          <h2>{article.Title}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        {/* Tous les boutons en haut */}
        <div className="article-actions-top">
          {!shouldHideButtons && (
            <button
              className="source-link-btn"
              onClick={handleOpenSource}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 11-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/>
                <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 10-4.243-4.243L6.586 4.672z"/>
              </svg>
              <span>Visit website</span>
            </button>
          )}

          
          <button 
            className={`readlater-btn ${isReadLater ? 'is-readlater' : ''}`}
            onClick={handleToggleReadLater}
            disabled={isLoading}
          >
            <BookmarkIcon filled={isReadLater} forceColor="#333333" />
            <span>{isReadLater ? 'Remove from reading list' : 'Read later'}</span>
          </button>
          
          <button 
            className={`favorite-btn ${isFavorite ? 'is-favorite' : ''}`}
            onClick={handleToggleFavorite}
            disabled={isLoading}
          >
            <HeartIcon filled={isFavorite} forceColor="black" />
            <span>{isFavorite ? 'Remove from favorites' : 'Add to favorites'}</span>
          </button>
          
          {!shouldHideButtons && (
            <button
              className="copy-link-btn"
              onClick={handleCopyLink}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 11-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/>
                <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 10-4.243-4.243L6.586 4.672z"/>
              </svg>
              <span>Copy link</span>
            </button>
          )}


          
          {!shouldHideButtons && (
            <button
              className="linkedin-share-btn"
              onClick={handleShareLinkedIn}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943
12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908
8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54
  0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z"/>
              </svg>
              <span>Share</span>
            </button>
            )}

        </div>
        
        <div className="article-detail-content">
          {/* Carrousel d'images */}
          <ImageCarousel images={images} isLocked={shouldBlur} />
          
          {/* Tags */}
          <div className="article-tags">
            <span className={`tag ${getTagColor('OEM')}`}>{article.OEM}</span>
            {article.brand && (
              <span className={`tag ${getTagColor('brand')}`}>{article.brand}</span>
            )}
            {article.car_model && (
              <span className={`tag ${getTagColor('car_model')}`}>{article.car_model}</span>
            )}
            {article.System && (
              <span className={`tag ${getTagColor('System')}`}>{article.System}</span>
            )}
            {(article.subsystem || article.Subsystem) && (
              <span className={`tag ${getTagColor('subsystem')}`}>{article.subsystem || article.Subsystem}</span>
            )}
            {article.part && (
              <span className={`tag ${getTagColor('part')}`}>{article.part}</span>
            )}
            {article.Type && (
              <span className={`tag ${getTagColor('Type')}`}>{article.Type}</span>
            )}
            {article.material && (
              <span className={`tag ${getTagColor('Material')}`}>{article.material}</span>
            )}
            {article.technology && (
              <span className={`tag ${getTagColor('technology')}`}>{article.technology}</span>
            )}
            {article.year_of_publication && (
              <span className="tag tag-year">{article.year_of_publication}</span>
            )}
          </div>
          
          <div className="article-info">
            {article.short_resume && (
              <div className="info-group">
                <span className="label">Short summary:</span>
                <p className="value">{article.short_resume}</p>
              </div>
            )}
          </div>
          
          {article.Long_resume && (
            <div className="article-long-resume">
              <h3>Long summary</h3>
              <div className="long-resume-content">
                {article.Long_resume}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Utiliser createPortal pour rendre au niveau du body
  return ReactDOM.createPortal(modalContent, document.body);
};

export default ArticleDetail;
